import { Button, RingProgress, Select, Skeleton, UnstyledButton } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconBinaryTree2, IconCalendar, IconFlag3Filled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay, parseDay } from '../../format.js';
import { useDatesLabels } from '../../inputs/dates.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { TOKENS } from '../../tokens.js';
import { ShipGlyph } from '../../VesselBadge.js';
import type { PersonnelVessel } from '../personnel/types.js';
import type { VesselParticulars } from '../pms/types.js';
import classes from './Compliance.module.css';
import {
  type CertificateBadge,
  certificateBadge,
  distribution,
  type UpcomingRow,
  type UpcomingTone,
} from './certificate-view.js';
import type { CertificateListItem, CertificateTypeRef, WindowState } from './types.js';

/** Kategori rozeti (referans "Type" sütunu): renkler `cert-*` token'ları. */
export function CategoryBadge({ type }: { type: CertificateTypeRef | null }) {
  const { t } = useTranslation();
  const badge: CertificateBadge = certificateBadge(type);
  return (
    <span className={classes.badge} data-badge={badge}>
      {t(`vesselCertificates.badges.${badge}`)}
    </span>
  );
}

export const STATUS_TONE: Record<CertificateListItem['status'], StatusTone> = {
  valid: 'success',
  expiring: 'warning',
  expired: 'danger',
};

/** Sertifikanın durum rozeti; gemide düzenlenen (ofis kaydı bekleyen) için ipucu. */
export function CertificateStatus({
  item,
}: {
  item: Pick<CertificateListItem, 'status' | 'superseded'>;
}) {
  const { t } = useTranslation();
  if (item.superseded) {
    return <StatusBadge tone="neutral">{t('vesselCertificates.status.superseded')}</StatusBadge>;
  }
  return (
    <StatusBadge tone={STATUS_TONE[item.status]}>
      {t(`vesselCertificates.status.${item.status}`)}
    </StatusBadge>
  );
}

export const WINDOW_TONE: Record<WindowState, StatusTone> = {
  upcoming: 'info',
  open: 'warning',
  overdue: 'danger',
};

export const UPCOMING_TONE: Record<UpcomingTone, StatusTone> = {
  expiring: 'warning',
  expired: 'danger',
  upcoming: 'info',
  open: 'warning',
  overdue: 'danger',
};

/** Kalan günün tonu: dolmak üzere ve geçmiş kırmızı (referans "21"). */
export function daysTone(item: Pick<CertificateListItem, 'status'>): 'warning' | 'danger' | null {
  return item.status === 'expiring' ? 'warning' : item.status === 'expired' ? 'danger' : null;
}

export interface CertificateVesselCardProps {
  particulars: VesselParticulars | null;
  vessels: readonly PersonnelVessel[];
  scope: 'web' | 'ship';
  range: [string | null, string | null];
  onRange: (range: [string | null, string | null]) => void;
  onVessel: (id: string) => void;
  /** ≤ 1023 px: ağaç çekmecede; düğme yalnız o genişlikte görünür. */
  onOpenTree: () => void;
}

/**
 * Üst kart (`gemi-sertifika-takibi.jpeg`): gemi görseli, ad ve IMO; Vessel Type, Flag, Port ve
 * Date Range (bitiş tarihi aralığı süzgeci). Web'de birden çok gemi varsa gemi seçici adın
 * yerindedir (`VesselCard` kalıbı). Gemi fotoğrafı yoktur: gemi kartının çizimi (M6d).
 */
export function CertificateVesselCard({
  particulars,
  vessels,
  scope,
  range,
  onRange,
  onVessel,
  onOpenTree,
}: CertificateVesselCardProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.card.${key}`, values);
  const labels = useDatesLabels();
  const dash = '—';
  const selector = scope === 'web' && vessels.length > 1;
  const name = particulars?.name ?? vessels[0]?.name ?? '';
  const imo = particulars?.imo ?? vessels[0]?.imo ?? null;
  return (
    <section className={classes.vesselCard} aria-label={name || k('vessel')}>
      <div className={classes.vesselIdentity}>
        <span className={classes.vesselThumb} aria-hidden>
          <ShipGlyph size={56} />
        </span>
        <div className={classes.vesselName}>
          {selector ? (
            <Select
              aria-label={k('vessel')}
              variant="unstyled"
              className={classes.vesselSelect}
              data={vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={particulars?.id ?? null}
              allowDeselect={false}
              comboboxProps={{ width: 260, position: 'bottom-start' }}
              onChange={(value) => {
                if (value) onVessel(value);
              }}
            />
          ) : (
            <h2 className={classes.vesselTitle} title={name}>
              {name}
            </h2>
          )}
          {imo ? <span className={classes.vesselImo}>{k('imo', { imo })}</span> : null}
        </div>
      </div>
      <dl className={classes.vesselFacts}>
        <div className={classes.vesselFact}>
          <dt>{k('vesselType')}</dt>
          <dd>{particulars?.vesselType ?? dash}</dd>
        </div>
        <div className={classes.vesselFact}>
          <dt>{k('flag')}</dt>
          <dd>
            <IconFlag3Filled size={16} className={classes.flagIcon} aria-hidden />
            {particulars?.flag ?? dash}
          </dd>
        </div>
        <div className={classes.vesselFact}>
          <dt>{k('port')}</dt>
          <dd>{particulars?.nextPort ?? dash}</dd>
        </div>
        <div className={classes.vesselFact} data-range>
          <dt id="cert-date-range">{k('dateRange')}</dt>
          <dd>
            <DatePickerInput
              type="range"
              aria-labelledby="cert-date-range"
              size="xs"
              w="100%"
              clearable
              allowSingleDateInRange
              valueFormat="DD.MM.YYYY"
              locale={labels.locale}
              previousLabel={labels.previousLabel}
              nextLabel={labels.nextLabel}
              placeholder={k('dateRangePlaceholder')}
              // Mantine'in yer tutucu rengi 4.5:1'i tutmaz (axe color-contrast)
              classNames={{ placeholder: classes.rangePlaceholder }}
              rightSection={
                range[0] || range[1] ? undefined : <IconCalendar size={15} aria-hidden />
              }
              value={range}
              onChange={(value) => {
                const [from, to] = value as [string | null, string | null];
                onRange([from ? from.slice(0, 10) : null, to ? to.slice(0, 10) : null]);
              }}
            />
          </dd>
        </div>
      </dl>
      <Button
        variant="default"
        className={classes.treeButton}
        leftSection={<IconBinaryTree2 size={16} aria-hidden />}
        onClick={onOpenTree}
      >
        {t('vesselCertificates.tree.open')}
      </Button>
    </section>
  );
}

/** Gün sayısının metni: süresizde "—". */
export function daysText(days: number | null): string {
  return days === null ? '—' : String(days);
}

export interface UpcomingPanelProps {
  rows: readonly UpcomingRow[] | null;
  onOpen: (id: string) => void;
  onViewAll: () => void;
}

/** "Upcoming Certificates" (referans): 90 gün içinde bitiş ya da sörvey; ilk beş satır. */
export function UpcomingPanel({ rows, onOpen, onViewAll }: UpcomingPanelProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.upcoming.${key}`, values);
  return (
    <Panel
      title={k('title')}
      icon={<IconCalendar size={18} />}
      extra={
        <Button variant="default" size="compact-xs" onClick={onViewAll}>
          {k('viewAll')}
        </Button>
      }
    >
      {rows === null ? (
        <div className={classes.skeletonRows} aria-hidden>
          {[0, 1, 2, 3].map((n) => (
            <Skeleton key={n} height={14} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title={k('empty')} />
      ) : (
        <section
          className={classes.tableBox}
          data-cards
          aria-label={k('table')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={`${classes.table} ${classes.upcomingTable}`} data-cards>
            <colgroup>
              <col data-col="due" />
              <col />
              <col data-col="type" />
              <col data-col="days" />
              <col data-col="status" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">{k('columns.dueDate')}</th>
                <th scope="col">{k('columns.name')}</th>
                <th scope="col">{k('columns.type')}</th>
                <th scope="col" data-numeric>
                  {k('columns.daysLeft')}
                </th>
                <th scope="col">{k('columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 5).map((row) => (
                <tr key={row.key}>
                  <td data-label={k('columns.dueDate')} className={classes.nowrap}>
                    {formatDay(row.dueOn)}
                  </td>
                  <td data-label={k('columns.name')}>
                    <UnstyledButton
                      className={classes.nameButton}
                      title={row.item.name}
                      onClick={() => onOpen(row.item.id)}
                    >
                      {row.item.name}
                    </UnstyledButton>
                    <span className={classes.kindNote}>
                      {k(`kinds.${row.kind}`, { defaultValue: row.kind })}
                    </span>
                  </td>
                  <td data-label={k('columns.type')}>
                    <CategoryBadge type={row.item.type} />
                  </td>
                  <td data-label={k('columns.daysLeft')} data-numeric>
                    {row.daysLeft}
                  </td>
                  <td data-label={k('columns.status')}>
                    <StatusBadge tone={UPCOMING_TONE[row.tone]}>
                      {k(`tones.${row.tone}`)}
                    </StatusBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </Panel>
  );
}

/** "Certificate Status Distribution": halka ve lejant (sayı ve yüzde koddan). */
export function DistributionPanel({
  summary,
}: {
  summary: { valid: number; expiring: number; overdue: number; total: number } | null;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.distribution.${key}`, values);
  const number = (value: number) =>
    new Intl.NumberFormat(i18n.resolvedLanguage ?? 'en').format(value);
  const parts = summary ? distribution(summary) : [];
  const color = {
    valid: TOKENS['success-fg'],
    expiring: TOKENS['warning-500'],
    overdue: TOKENS['danger-fg'],
  } as const;
  const tone = { valid: 'success', expiring: 'warning', overdue: 'danger' } as const;
  const sum = parts.reduce((acc, p) => acc + p.value, 0);
  return (
    <Panel title={k('title')}>
      {summary === null ? (
        <Skeleton height={150} />
      ) : (
        <div className={classes.distribution}>
          <RingProgress
            size={150}
            thickness={20}
            roundCaps={false}
            aria-label={k('label')}
            sections={
              sum === 0
                ? [{ value: 100, color: TOKENS['page-bg'] }]
                : parts
                    .filter((p) => p.value > 0)
                    .map((p) => ({ value: (p.value / sum) * 100, color: color[p.key] }))
            }
            label={
              <div className={classes.ringCenter}>
                <span className={classes.ringTotal}>{number(summary.total)}</span>
                <span className={classes.ringLabel}>{k('total')}</span>
              </div>
            }
          />
          <dl className={classes.legend}>
            {parts.map((p) => (
              <div key={p.key} style={{ display: 'contents' }}>
                <dt>
                  <span className={classes.dot} data-tone={tone[p.key]} aria-hidden />
                  {k(p.key)}
                </dt>
                <dd>{k('value', { value: number(p.value), percent: p.percent })}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </Panel>
  );
}

/** Gün girişinin metinden okunması (DatePickerInput yazılırsa). */
export const parseDayText = parseDay;
